// src/components/Presentation/SlideCanvas.js (Pure Rendering)
import React from 'react';
import { Plus, Type, Image as ImageIcon, Video, BarChart3, Users, MessageCircle } from 'lucide-react';

const SlideCanvas = ({ 
  slide, 
  selectedElement, 
  onSelectElement, 
  selectedContentType, 
  onAddContent,
  uploadingImage 
}) => {
  const handleSlateClick = (slate) => {
    if (selectedContentType && slate.type === 'empty') {
      onAddContent(slate.id, selectedContentType);
    } else {
      onSelectElement(slate.id);
    }
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-2xl overflow-hidden relative"
      style={{ width: '900px', height: '675px', maxWidth: '90vw', maxHeight: '80vh' }}
    >
      {uploadingImage && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="text-gray-900">Uploading image...</span>
          </div>
        </div>
      )}
      
      <div 
        className="w-full h-full relative p-6"
        style={{ backgroundColor: slide.background?.value || '#ffffff' }}
      >
        <div 
          className="grid grid-cols-12 grid-rows-12 gap-3 h-full w-full"
          style={{ gridTemplateRows: 'repeat(12, 1fr)' }}
        >
          {slide.slates?.map((slate) => (
            <SlateRenderer
              key={slate.id}
              slate={slate}
              isSelected={selectedElement === slate.id}
              onSelect={() => handleSlateClick(slate)}
              selectedContentType={selectedContentType}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const SlateRenderer = ({ slate, isSelected, onSelect, selectedContentType }) => {
  return (
    <div
      onClick={onSelect}
      className={`
        relative border-2 rounded-lg transition-all duration-200 cursor-pointer overflow-hidden
        ${isSelected 
          ? 'border-indigo-500 ring-2 ring-indigo-200 z-20' 
          : slate.type === 'empty' 
            ? 'border-dashed border-gray-300 hover:border-gray-400' 
            : 'border-gray-200 hover:border-gray-300'
        }
        ${selectedContentType && slate.type === 'empty' 
          ? 'bg-indigo-50 border-indigo-300 animate-pulse' 
          : ''
        }
      `}
      style={{
        gridColumn: `${slate.gridArea.x + 1} / span ${slate.gridArea.w}`,
        gridRow: `${slate.gridArea.y + 1} / span ${slate.gridArea.h}`,
        minHeight: '40px'
      }}
    >
      {slate.type === 'empty' ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center text-gray-400">
            <Plus className="w-8 h-8 mx-auto mb-2" />
            <span className="text-sm font-medium">
              {selectedContentType ? `Click to add ${selectedContentType}` : 'Add content'}
            </span>
          </div>
        </div>
      ) : (
        <SlateContent slate={slate} />
      )}
    </div>
  );
};

const SlateContent = ({ slate }) => {
  switch (slate.type) {
    case 'text':
      return (
        <div className="p-4 h-full overflow-hidden">
          <div 
            className={`
              ${slate.content?.fontSize || 'text-lg'} 
              ${slate.content?.fontWeight || 'font-normal'}
              ${slate.content?.fontStyle === 'italic' ? 'italic' : ''}
              text-${slate.content?.alignment || 'left'}
            `}
            style={{ 
              color: slate.content?.color || '#000000',
              textDecoration: slate.content?.textDecoration || 'none'
            }}
          >
            {slate.content?.content || 'Click to edit text'}
          </div>
        </div>
      );
    
    case 'image':
      return (
        <div className="w-full h-full overflow-hidden">
          {slate.content?.src ? (
            <img 
              src={slate.content.src} 
              alt={slate.content.alt || ''} 
              className="w-full h-full object-cover"
              style={{ objectFit: slate.content?.objectFit || 'cover' }}
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                <span className="text-sm">Click to add image</span>
              </div>
            </div>
          )}
        </div>
      );

    case 'video':
      return (
        <div className="w-full h-full bg-gray-900 flex items-center justify-center">
          {slate.content?.src ? (
            <video 
              src={slate.content.src}
              controls
              className="max-w-full max-h-full"
            />
          ) : (
            <div className="text-center text-gray-300">
              <Video className="w-12 h-12 mx-auto mb-2" />
              <span className="text-sm">Click to add video</span>
            </div>
          )}
        </div>
      );

    case 'poll':
      return (
        <div className="p-4 h-full overflow-auto">
          <h3 className="font-bold text-lg mb-4">{slate.content?.question || 'Poll Question'}</h3>
          <div className="space-y-3">
            {slate.content?.options?.map((option, index) => (
              <div key={index} className="p-3 bg-gray-100 rounded-lg text-sm">
                <span className="text-gray-600 mr-2">{String.fromCharCode(65 + index)}.</span>
                {option}
              </div>
            ))}
          </div>
        </div>
      );

    default:
      return (
        <div className="w-full h-full bg-gray-50 flex items-center justify-center">
          <span className="text-sm text-gray-500 capitalize">{slate.type} content</span>
        </div>
      );
  }
};

export default SlideCanvas;